import {
  getFooterData,
  getHeaderData,
  getSisNavigationMenu,
  getTaxonomyData,
  getTransversalData,
} from '@digital-retail/falabella-ui-cross-components';
import { getUrlParts, isSodimacRetail, isStandalone } from '@digital-retail/store-manager';
import { cache } from '../@lib/cache';
import apiConfig from '../config/api/apiConfig';
import constants from '../config/constants';
import { siteConfig } from '../static/siteConfig.default';
import { isWindow } from './deviceChecker';
import { httpService } from './httpService';
import _ from './LodashImports';
import Logger from './Logger';

const fetchConfigFromApi = async (config, appCtx, url, headers, opt = {}, fallback = {}) => {
  if (config.ENABLE_MEM_CACHE) {
    const valueFromCache = await cache.get(url);
    if (!valueFromCache) {
      const valueFromApi = await httpService(appCtx).get(url, {
        headers,
        ...opt,
      });
      await cache.set(url, valueFromApi);
      return valueFromApi;
    }
    return valueFromCache;
  }
  return httpService(appCtx).get(
    url,
    {
      headers,
      ...opt,
    },
    'text',
    fallback
  );
};

const isZoneDataInvalid = (zoneData = {}, isTottus = false, store = '') => {
  const { politicalId, priceGroupId, zones = [], expressZones = [], standardZones = [] } = zoneData;

  const hasRequiredFields =
    politicalId && priceGroupId && Array.isArray(zones) && (!isSodimacRetail(store) ? zones?.length > 0 : true);
  if (!hasRequiredFields) {
    return true;
  }
  const hasValidExpressAndStandardZones = Array.isArray(expressZones) && Array.isArray(standardZones);

  if (isTottus && !hasValidExpressAndStandardZones) {
    return true;
  }

  return false;
};

const appServices = (publicRuntimeConfig, serverRuntimeConfig, appCtx) => ({
  siteConfig: {
    init: async () => {
      const { getAccessToken } = serverRuntimeConfig;
      const { regionCode, store } = appCtx;
      const st = store?.toLowerCase() || 'marketplace';
      const tokenResponse = await getAccessToken({
        tenant: regionCode,
        store: st,
        context: 'browse',
      });
      const storePath = store ? `/${store}` : '/marketplace';
      const siteConfigUrl = `${publicRuntimeConfig.SITE_CONFIG_URL}${storePath}?country=${regionCode}&parseJson=true&merge=true`;
      return fetchConfigFromApi(serverRuntimeConfig, appCtx, siteConfigUrl, {
        Authorization: tokenResponse,
      });
    },
    resolve: ({ data, error }) => {
      if (error) {
        Logger.error(`Using fallback site-config => ${JSON.stringify(error)}`);
        return siteConfig[appCtx.regionCode || 'cl'][appCtx.store || 'marketplace'];
      }
      return data;
    },
  },
  transversalBanner: {
    init: async () => {
      return getTransversalData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  header: {
    init: async () => {
      return getHeaderData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
        store: appCtx.store,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  sisNavigationMenu: {
    init: async () => {
      return appCtx.store
        ? getSisNavigationMenu({
            tenant: appCtx.regionCode,
            baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
            store: appCtx.store,
          })
        : {};
    },
    resolve: (data) => {
      return data;
    },
  },
  footer: {
    init: async () => {
      return getFooterData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
        store: appCtx.store,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  taxonomy: {
    init: async () => {
      return getTaxonomyData({
        tenant: appCtx.regionCode,
        baseUrl: publicRuntimeConfig.CLUSTER_CONTENT_API_HOST,
      });
    },
    resolve: (data) => {
      return data;
    },
  },
  geo: {
    init: () => {
      // Changed this call to new API
      const getLocationsUrl = apiConfig.getGeoUrl({
        base: isWindow()
          ? publicRuntimeConfig.CUSTOMER_LOCATION_HOST
          : publicRuntimeConfig.CLUSTER_CUSTOMER_LOCATION_HOST,
        siteId: appCtx.regionCode,
        store: appCtx.store,
      });

      return fetchConfigFromApi(serverRuntimeConfig, appCtx, getLocationsUrl);
    },
    resolve: ({ data, error }) => {
      if (error) {
        Logger.error(`Using default zones => ${JSON.stringify(error)}`);
        return {
          defaultZone: constants.ANDES_DEF_ZONE,
        };
      }
      const { default: defaultZone } = data;
      return { defaultZone };
    },
  },
  comuna: {
    init: () => {
      const { store, regionCode, pid, latLong, isTottusSA, isDefaultComuna = false } = appCtx;
      const environment = publicRuntimeConfig.ENVIRONMENT || 'production';
      // eslint-disable-next-line no-underscore-dangle
      const _store = isStandalone({ store }) || isSodimacRetail(store) ? store : '';
      const { origin } = getUrlParts({ store: _store, environment, regionCode });
      const base = `${origin}${publicRuntimeConfig.CL_ENDPOINT}`;
      const getComunaUrl = apiConfig.getDistrictInfo({
        base,
        siteId: regionCode,
        politicalId: isDefaultComuna ? 'default' : pid,
        latLong,
        getAllZones: isTottusSA,
        store,
      });

      return fetchConfigFromApi(serverRuntimeConfig, appCtx, getComunaUrl);
    },
    resolve: ({ data, error }) => {
      const { regionCode, isTottusSA, store } = appCtx;
      const zoneData = _.get(data, 'data', {});

      if (isZoneDataInvalid(zoneData, isTottusSA, store)) {
        Logger.error(`Using hardcoded default zones from catch block => ${JSON.stringify(error)}`);
        return {
          data: constants.DEF_ZONE[regionCode][process.env.ENVIRONMENT],
          default: true,
          fromCatch: true,
        };
      }
      return data;
    },
  },
  virtualZones: {
    init: () => {
      const { regionCode, pid, isSodimacRE, store } = appCtx;
      if (isSodimacRE) {
        const environment = publicRuntimeConfig.ENVIRONMENT || 'production';

        const { origin } = getUrlParts({ store, environment, regionCode });
        const base = `${origin}${publicRuntimeConfig.CL_ENDPOINT}`;
        const getComunaUrl = apiConfig.getVirtualZonesUrl({
          base,
          regionCode,
          politicalId: pid,
        });

        return fetchConfigFromApi(serverRuntimeConfig, appCtx, getComunaUrl);
      }
      return {
        data: [],
      };
    },
    resolve: ({ data, error }) => {
      if (error) {
        Logger.error(`Unable to get virtualZones => ${JSON.stringify(error)}`);
        return { data: [] };
      }
      return data;
    },
  },
});
// ! Must be called only on server
const fetchApplicationData = (publicRuntimeConfig, serverRuntimeConfig, appCtx, query) => ({
  fetch: async (services) => {
    const { tracingHeaders } = appCtx;
    const fetcher = appServices(publicRuntimeConfig, serverRuntimeConfig, appCtx, query);
    const promises = services
      .filter((s) => fetcher[s])
      .map((service) => {
        return fetcher[service].init();
      });
    let data;
    try {
      data = await Promise.all(promises);
    } catch (e) {
      Logger.error(`Error Running Promise => ${e}`, tracingHeaders);
    }

    // eslint-disable-next-line consistent-return
    return services.map((service, i) => {
      return fetcher[service].resolve(data[i]);
    });
  },
});
export { fetchApplicationData, isZoneDataInvalid };
